import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PayoutCarousel - Infinite scrolling carousel for displaying payout certificates
 * Loads data from API - managed via Admin Dashboard
 * Speed and delay configurable from Admin Settings
 */
export const PayoutCarousel = ({ openModal }) => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [animationStarted, setAnimationStarted] = useState(false);
  const [settings, setSettings] = useState({ speed: 0.8, initialDelay: 2000 });
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  
  // Configuration
  const ITEM_WIDTH = 336; // 320px width + 16px gap
  
  // Fetch settings and payouts from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch settings first
        const settingsRes = await fetch(`${API_URL}/api/settings/carousel`);
        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          setSettings({
            speed: settingsData.speed || 0.8,
            initialDelay: settingsData.initial_delay || 2000
          });
        }
        
        // Fetch payouts
        const response = await fetch(`${API_URL}/api/payouts`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            setPayouts(data);
            // Start position: first item (order 0) starts off-screen to the right
            setScrollPosition(ITEM_WIDTH * (data.length - 1) + 250);
          }
        }
      } catch (error) {
        console.log('Error fetching data:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, []);
  
  // Calculate dimensions based on payouts
  const totalItems = payouts.length;
  const totalWidth = ITEM_WIDTH * totalItems;
  
  // Auto-scroll animation - uses speed from settings
  const animate = useCallback(() => {
    if (totalWidth === 0) return;
    setScrollPosition(prev => {
      const newPos = prev + settings.speed;
      if (newPos >= totalWidth) {
        return newPos - totalWidth;
      }
      return newPos;
    });
    animationRef.current = requestAnimationFrame(animate);
  }, [totalWidth, settings.speed]);
  
  useEffect(() => {
    if (payouts.length > 0 && !animationStarted) {
      // Add initial delay before starting animation (from settings)
      const delayTimer = setTimeout(() => {
        setAnimationStarted(true);
        animationRef.current = requestAnimationFrame(animate);
      }, settings.initialDelay);
      
      return () => clearTimeout(delayTimer);
    } else if (animationStarted) {
      animationRef.current = requestAnimationFrame(animate);
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate, payouts.length, animationStarted, settings.initialDelay]);
  
  // Arrow navigation
  const goLeft = useCallback(() => {
    setScrollPosition(prev => {
      const newPos = prev - ITEM_WIDTH;
      if (newPos < 0) {
        return newPos + totalWidth;
      }
      return newPos;
    });
  }, [totalWidth]);
  
  const goRight = useCallback(() => {
    setScrollPosition(prev => {
      const newPos = prev + ITEM_WIDTH;
      if (newPos >= totalWidth * 2) {
        return newPos - totalWidth;
      }
      return newPos;
    });
  }, [totalWidth]);
  
  // Loading/empty state
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto mb-16">
        <div className="bg-gradient-to-br from-yellow-900/30 via-yellow-800/20 to-gray-900 border border-yellow-600/30 rounded-lg p-8 text-center">
          <p className="text-gray-400">Loading payouts...</p>
        </div>
      </div>
    );
  }
  
  if (payouts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto mb-16">
        <div className="bg-gradient-to-br from-yellow-900/30 via-yellow-800/20 to-gray-900 border border-yellow-600/30 rounded-lg p-8 text-center">
          <p className="text-gray-400">No payouts to display</p>
        </div>
      </div>
    );
  }
  
  // Triple items for infinite loop
  const allItems = [...payouts, ...payouts, ...payouts];
  
  const renderItem = (item, idx) => {
    // Build full image URL (handle /api/images paths and external URLs)
    const getFullUrl = (path) => {
      if (!path) return null;
      if (path.startsWith('/api/') || path.startsWith('/uploads/')) {
        return `${API_URL}${path}`;
      }
      return path;
    };
    
    const imgSrc = getFullUrl(item.img);
    const imgFullSrc = getFullUrl(item.img_full) || imgSrc;
    
    return (
      <div key={idx} className="flex-shrink-0 w-[320px] flex flex-col justify-center" style={{ marginRight: '16px' }}>
        <img 
          src={imgSrc}
          alt={item.alt}
          className={`w-full ${item.type === 'hive' ? 'aspect-square object-cover' : ''} rounded-lg shadow-xl cursor-pointer hover:opacity-90 transition-opacity`}
          onClick={() => openModal(imgFullSrc, item.alt)}
        />
        <a 
          href={item.link} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="block text-center text-cyan-400 hover:text-cyan-300 text-sm font-medium mt-3 bg-cyan-500/10 py-2 rounded"
        >
          {item.link_text}
        </a>
      </div>
    );
  };
  
  return (
    <div className="max-w-4xl mx-auto mb-16" data-testid="payout-carousel">
      <div className="bg-gradient-to-br from-yellow-900/30 via-yellow-800/20 to-gray-900 border border-yellow-600/30 rounded-lg p-8 relative overflow-hidden">
        
        <p className="text-center text-sm mb-6 mt-2 flex items-center justify-center gap-3 flex-wrap">
          <span className="text-gray-400">SOME REWARDS ON SOME FIRMS</span>
          <span className="text-gray-600">•</span>
          <span className="bg-yellow-500 text-black px-2 py-1 text-xs font-bold rounded whitespace-nowrap">FUNDING PIPS GOLD CERTIFICATE</span>
        </p>
        
        {/* Carousel */}
        <div className="relative">
          {/* Left Arrow */}
          <button 
            onClick={goLeft}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/80 text-white p-2 rounded-full transition-all hover:scale-110"
            data-testid="carousel-left-arrow"
          >
            <ChevronLeft size={28} />
          </button>
          
          {/* Right Arrow */}
          <button 
            onClick={goRight}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/80 text-white p-2 rounded-full transition-all hover:scale-110"
            data-testid="carousel-right-arrow"
          >
            <ChevronRight size={28} />
          </button>
          
          {/* Carousel Container */}
          <div className="overflow-hidden mx-12" ref={containerRef}>
            <div 
              className="flex items-center"
              style={{
                transform: `translateX(-${scrollPosition}px)`,
              }}
            >
              {allItems.map((item, idx) => renderItem(item, idx))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayoutCarousel;
